import PCLNAnalytics from '@pcln/analytics'
import debounce from 'lodash/debounce'

import { getDeviceWidth } from '../util/displayHelpers'
import { flySliceSelectionStorage } from '../components/Legacy/fly-slice-selection-storage/fly-slice-selection-storage'

const scrollTopOnFilterChange = () => {
  const DESKTOP_WIDTH = 1024
  const deviceWidth = getDeviceWidth()

  if (deviceWidth >= DESKTOP_WIDTH) {
    const debouncedScroll = debounce(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }))

    debouncedScroll()
  }
}

function scrollTopWhenRequired(type) {
  switch (type) {
    case 'sliceSelection':
      window.scrollTo(0, 0)
      break
    case 'filter':
      scrollTopOnFilterChange()
      break
    default:
      break
  }
}

function getExdCarriersGraph(displayCarriers, airlines) {
  return displayCarriers.map((carrier) => {
    const airlineObj = airlines[carrier]
    airlineObj.smallImage = airlineObj.smallImage + '?opto&auto=webp'
    return {
      image: airlineObj.smallImage,
      name: airlineObj.name,
    }
  })
}

/**
 * Do Not delete this file as part of any cleanup
 * still used by SBS Trip Types (Round trip) and contained UpdatedFilterParams
 * */
function buildFlySearchDisplayFiltersObj({
  filter,
  activeTabId,
  isSliceSelection,
  airSearchRequestModel,
  PCLN_BOOTSTRAP_DATA,
}) {
  if (!filter.defaults) {
    PCLNAnalytics.log({
      event: 'Build airSearchReq filter defaults missing',
      action: JSON.stringify(filter.originalValues),
    })
    return null
  }

  const newFilters = flySliceSelectionStorage.getFormattedSliceFilters()
  const { values: filterValues = {}, defaults = {} } = filter
  const { AIRLINES } = defaults

  const filterObj = {
    sliceId: activeTabId,
  }

  // Preserve/persist selected stops in request obj
  const previousActiveTabId = activeTabId - 1
  let stopsReadIndex = activeTabId
  if (isSliceSelection) {
    stopsReadIndex = previousActiveTabId
  }

  filterObj.numOfStops = parseInt(filterValues[`numOfStops${stopsReadIndex}`] || '7')
  filterObj.stopsFilterType = 'MAX'

  // Airlines Filters Per Slice
  const inclCarriers = 'includedCarriers' + activeTabId
  const exclCarriers = 'excludedCarriers' + activeTabId

  if (
    !isSliceSelection &&
    filterValues[inclCarriers] &&
    filterValues[inclCarriers].length &&
    filterValues[inclCarriers].length !== AIRLINES.length
  ) {
    filterObj.includedCarriers = filterValues[inclCarriers]
  }

  // If included-airlines in URL, persist across all requests (slice selection, filter, load more)
  if (
    PCLN_BOOTSTRAP_DATA?.searchRequest?.params?.['included-airlines'] &&
    filterValues?.['includedCarriers']?.length > 0
  ) {
    filterObj.includedCarriers = filterValues['includedCarriers']
  }

  /**
   * Included filter values array should not be zero when excluded filters are considered
   * */
  if (filterValues[inclCarriers]?.length && filterValues[exclCarriers] && filterValues[exclCarriers].length) {
    filterObj.excludedCarriers = filterValues[exclCarriers]
  }

  if (filterValues['origAirports' + activeTabId] && filterValues['origAirports' + activeTabId].length) {
    filterObj.origAirports = filterValues['origAirports' + activeTabId]
  }

  if (filterValues['destAirports' + activeTabId] && filterValues['destAirports' + activeTabId].length) {
    filterObj.destAirports = filterValues['destAirports' + activeTabId]
  }

  if (filterValues?.[`timebands${activeTabId}`]) {
    filterObj.timebands = [
      {
        takeoff: filterValues?.[`timebands${activeTabId}`]?.takeoff || [],
        landing: filterValues?.[`timebands${activeTabId}`]?.landing || [],
      },
    ]
  }

  newFilters.push(filterObj)

  const excludedDates = filterValues.excludedDates

  airSearchRequestModel.clearExactDateFilters()

  const exactDateFilters = []
  if (excludedDates && excludedDates.length) {
    const re = RegExp('[0-9]{4}-[0-9]{2}-[0-9]{2}', 'g')

    for (let i = 0; i < excludedDates.length; i++) {
      const splitKey = excludedDates[i].match(re)
      const excludedDateCombo = { slice1DepartDate: splitKey[0] }

      if (splitKey.length === 2) {
        excludedDateCombo.slice2DepartDate = splitKey[1]
      }

      exactDateFilters.push(excludedDateCombo)
    }
  }

  updateFiltersWithIncludedAttributes(newFilters, filter)
  airSearchRequestModel.setSliceFilters({ sliceFilters: newFilters })
  airSearchRequestModel.setExactDateFilters({ exactDateFilters })
}

export function updateFiltersWithIncludedAttributes(newFilters, filter) {
  newFilters.forEach((newFilter, index) => {
    newFilter.attributes = filter.sliceFilters[index].includedAttributes || []
  })
}

function crossSellPosition(nodesCount, exdMoreThanRetail, firstExdIter, hasExpressDeal, counter) {
  const everyTenListings = counter % 10 === 0
  const everyTenNodes = nodesCount % 10 === 0
  const betweenNinthTenthListing = counter < 10 ? counter % 9 === 0 : (counter - 1) % 9 === 0
  const everyFifthListing = nodesCount % 6 === 0
  const minimumNodes = nodesCount > 3
  const initialHigherExd =
    hasExpressDeal && minimumNodes && betweenNinthTenthListing && exdMoreThanRetail && firstExdIter
  const initialLowerExd =
    hasExpressDeal && minimumNodes && everyFifthListing && !exdMoreThanRetail && firstExdIter
  return {
    minimumNodes,
    everyTenListings,
    initialHigherExd,
    initialLowerExd,
    betweenNinthTenthListing,
    everyTenNodes,
  }
}

function crossSellItinPush(
  minimumNodes,
  everyTenListings,
  initialHigherExd,
  initialLowerExd,
  betweenNinthTenthListing,
  everyTenNodes,
  hasExpressDeal,
  index,
  exdMoreThanRetail,
  firstExdIter,
  showCrossSellBannerSeti
) {
  if (showCrossSellBannerSeti) {
    if (index === 5 || (index > 0 && index % 10 === 0)) {
      return true
    }
  } else {
    if (hasExpressDeal) {
      if (exdMoreThanRetail && betweenNinthTenthListing && index < 10 && !firstExdIter) {
        return true
      }
      if (minimumNodes && !exdMoreThanRetail && index > 11 && everyTenNodes) {
        return true
      }
      if (minimumNodes && exdMoreThanRetail && index > 11 && everyTenNodes) {
        return true
      }
    }
    if (minimumNodes && everyTenListings && !hasExpressDeal) {
      return true
    }
    if (initialHigherExd || (initialLowerExd && index < 7)) {
      return true
    }
    return false
  }
}

function buildCheckoutQuery({
  itinerary,
  adults,
  children,
  infants,
  cabinClass,
  setiOverride,
  setiOverrideDefault,
  refclickid,
  desktopExD,
  tripType,
  searchType,
  cartKey,
  uspCacheKey,
  flyComponentIndex,
  componentKey,
  bundleType,
}) {
  const { refKey } = itinerary
  const data = {
    'ref-key': refKey,
    'num-adults': adults,
    'num-children': children,
    'num-infants': infants,
    'itinerary-type-code': tripType,
    'cabin-class': cabinClass,
    'search-type': searchType,
    type: itinerary.isExpressDeal ? 'edm' : 'rm',
  }
  if (itinerary.priceKey) {
    data['price-key'] = itinerary.priceKey
    data['item-key'] = itinerary.itemKey
  }
  // Do not store if not present
  if (setiOverride) {
    data.seti = setiOverride
  }
  if (setiOverrideDefault) {
    data['seti-default'] = setiOverrideDefault
  }
  if (refclickid && window.location.host.includes('booking')) {
    data.refclickid = refclickid
  }
  if (cartKey) {
    data['cart-key'] = cartKey
  }
  if (uspCacheKey) {
    data['usp-cache-key'] = uspCacheKey
  }
  if (componentKey) {
    data['component-key'] = componentKey
  }
  if (flyComponentIndex) {
    data['component-index'] = flyComponentIndex
  }
  if (bundleType) {
    data['bundle-type'] = bundleType
  }
  data.type = desktopExD === 'edd' ? desktopExD : data.type // TODO: Is this needed

  const params = new URLSearchParams(data).toString()
  return `?${params}`
}

function buildMap(arrayFromGraph) {
  const mapFromArray = {}
  arrayFromGraph?.forEach?.((eachElement) => {
    const code = eachElement?.code
    if (code) {
      mapFromArray[code] = eachElement
    }
  })
  return mapFromArray
}

export {
  scrollTopWhenRequired,
  getExdCarriersGraph,
  crossSellPosition,
  crossSellItinPush,
  buildCheckoutQuery,
  buildFlySearchDisplayFiltersObj,
  buildMap,
}
